import React, { useState, useMemo } from 'react';
import { Card, Button, Space, Tabs, message, Descriptions, Tag, Alert } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChangePasswordForm, TwoFactorSetupDialog, UserPermissionMatrix, createAuthApi } from '@zero/auth-admin-ui';
import { usePermissions } from '@zero/auth-client';
import { useGridPageSize } from '../../hooks/useGridPageSize.js';

export function ProfilePage({ axios }) {
  const api = useMemo(() => createAuthApi(axios), [axios]);
  const { t } = useTranslation();
  const { context } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  // force=1: admin vừa Reset pass ⇒ BUỘC đổi mật khẩu trước khi dùng tiếp (chỉ hiện tab password).
  const force = searchParams.get('force') === '1';
  const activeTab = force ? 'password'
    : (['info', 'permissions', 'password', '2fa'].includes(searchParams.get('tab'))
        ? searchParams.get('tab') : 'info');
  const [twoFaOpen, setTwoFaOpen] = useState(false);
  const [permPageSize, setPermPageSize] = useGridPageSize('myPermissions', 25);

  const handleChange = async (oldPw, newPw) => {
    const res = await api.changeMyPassword(oldPw, newPw);
    // Đổi xong: thư viện đã xóa cờ MustChangePassword ⇒ rời màn ép đổi, về trang chủ.
    if (force) setTimeout(() => { window.location.href = '/fms/'; }, 600);
    return res;
  };
  const handleSetup  = () => api.setup2Fa().then((r) => r.data?.Data ?? r.data);
  const handleVerify = (code) => api.verify2Fa(code);
  const handleDisable= async () => {
    await api.disable2Fa();
    message.success('2FA disabled');
  };

  if (!context) return null;

  const tabItems = [
      {
        key: 'info', label: t('app:menu.profile'),
        children: (
          <Card size="small">
            <Descriptions size="small" bordered column={1}>
              <Descriptions.Item label={t('user.username')}>{context.Username}</Descriptions.Item>
              {context.FullName && (
                <Descriptions.Item label={t('user.fullName')}>{context.FullName}</Descriptions.Item>
              )}
              {context.Email && (
                <Descriptions.Item label={t('user.email')}>{context.Email}</Descriptions.Item>
              )}
              <Descriptions.Item label="User ID">{context.UserID}</Descriptions.Item>
              <Descriptions.Item label={t('app:dashboard.permissions')}>{context.Permissions?.length ?? 0}</Descriptions.Item>
            </Descriptions>
          </Card>
        ),
      },
      {
        key: 'permissions', label: t('app:dashboard.myPermissions'),
        children: (
          <Card size="small">
            <UserPermissionMatrix
              permissions={context.Permissions ?? []}
              pageSize={permPageSize}
              onPageSizeChange={setPermPageSize}
              scrollY="calc(100vh - 250px)"
            />
          </Card>
        ),
      },
      {
        key: 'password', label: t('changePw.title'),
        children: <ChangePasswordForm onChange={handleChange} />,
      },
      {
        key: '2fa', label: '2FA',
        children: (
          <Space>
            <Button type="primary" onClick={() => setTwoFaOpen(true)}>{t('2fa.setup.title')}</Button>
            <Button onClick={handleDisable}>Disable 2FA</Button>
            <TwoFactorSetupDialog
              open={twoFaOpen}
              onClose={() => setTwoFaOpen(false)}
              onSetup={handleSetup}
              onVerify={handleVerify}
            />
          </Space>
        ),
      },
  ];

  const shown = force ? tabItems.filter((it) => it.key === 'password') : tabItems;

  return (
    <>
      {force && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('pwAdmin.force.title')}
          description={t('pwAdmin.force.desc')}
        />
      )}
      <Tabs
        activeKey={activeTab}
        onChange={(k) => setSearchParams(k === 'info' ? {} : { tab: k }, { replace: true })}
        items={shown}
      />
    </>
  );
}
